import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, KeyRound, Trash2, Upload, User, X } from 'lucide-react';
import { api } from './api.js';
import { AppShell, TaskDrawer } from './components/AppShell.jsx';
import { Avatar, Button, Callout, IconButton, Modal, Skeleton } from './components/ui.jsx';
import OverviewPage from './pages/OverviewPage.jsx';
import CandidatesPage from './pages/CandidatesPage.jsx';
import CandidateDetail from './pages/CandidateDetail.jsx';
import ImportPage from './pages/ImportPage.jsx';
import JobsPage from './pages/JobsPage.jsx';
import ModelsPage from './pages/ModelsPage.jsx';
import CalibrationPage from './pages/CalibrationPage.jsx';
import TalentLibraryPage from './pages/TalentLibraryPage.jsx';
import AuditPage from './pages/AuditPage.jsx';
import AccountsPage from './pages/AccountsPage.jsx';
import DepartmentsPage from './pages/DepartmentsPage.jsx';
import AuthPage from './pages/AuthPage.jsx';
import { buildRouteHash, parseRouteHash } from './routes.js';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, useUiLocalization } from './i18n.js';

const PAGE_ROLES = {
  overview: ['admin', 'hr', 'hiring_manager'],
  candidates: ['admin', 'hr', 'hiring_manager'],
  candidate: ['admin', 'hr', 'hiring_manager'],
  import: ['admin', 'hr', 'hiring_manager'],
  jobs: ['admin', 'hr', 'hiring_manager'],
  calibration: ['admin', 'hiring_manager'],
  'talent-library': ['admin', 'hr', 'hiring_manager'],
  accounts: ['admin'],
  departments: ['admin'],
  models: ['admin'],
  audit: ['admin'],
};

function routeFromHash() {
  return parseRouteHash(window.location.hash);
}

function PasswordModal({ open, onClose, onSaved, notify }) {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const close = () => {
    setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setError('');
    onClose();
  };
  const save = async () => {
    setError('');
    if (form.newPassword !== form.confirmPassword) return setError('两次输入的新密码不一致。');
    setBusy(true);
    try {
      const user = await api.changePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
      notify('密码已更新，其他登录会话已撤销');
      onSaved(user);
      close();
    } catch (cause) {
      setError(cause.message);
    } finally {
      setBusy(false);
    }
  };
  return <Modal open={open} title="修改登录密码" description="更新后会保留当前设备，并撤销该账户的其他会话。" onClose={close} footer={<><Button variant="ghost" onClick={close}>取消</Button><Button variant="primary" icon={KeyRound} loading={busy} onClick={save}>更新密码</Button></>}>
    <div className="modal-form password-modal-form">
      {error && <div className="modal-form-full"><Callout tone="danger" title="无法更新密码">{error}</Callout></div>}
      <label className="modal-form-full"><span>当前密码</span><input type="password" autoComplete="current-password" value={form.currentPassword} onChange={(event) => update('currentPassword', event.target.value)} /></label>
      <label><span>新密码</span><input type="password" autoComplete="new-password" value={form.newPassword} onChange={(event) => update('newPassword', event.target.value)} /></label>
      <label><span>确认新密码</span><input type="password" autoComplete="new-password" value={form.confirmPassword} onChange={(event) => update('confirmPassword', event.target.value)} /></label>
      <small className="modal-form-full password-guidance">至少 10 个字符，同时包含字母和数字。</small>
    </div>
  </Modal>;
}

function ProfileModal({ open, onClose, user, onSaved, notify, locale, setLocale }) {
  const [name, setName] = useState(user?.name || '');
  const [defaultLocale, setDefaultLocale] = useState(user?.defaultLocale || 'en');
  const [avatarPreview, setAvatarPreview] = useState(user?.avatarUrl || null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [clearAvatar, setClearAvatar] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setName(user?.name || '');
    setDefaultLocale(user?.defaultLocale || 'en');
    setAvatarPreview(user?.avatarUrl || null);
    setAvatarFile(null);
    setClearAvatar(false);
    setError('');
  }, [user, open]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(png|jpe?g|webp|gif)$/i.test(file.name)) {
      setError('仅支持 PNG、JPG、WEBP、GIF 格式图片。');
      return;
    }
    setAvatarFile(file);
    setClearAvatar(false);
    setAvatarPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setClearAvatar(true);
    setAvatarPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const save = async () => {
    if (!name.trim()) return setError('姓名不能为空。');
    setBusy(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('name', name.trim());
      formData.append('defaultLocale', defaultLocale);
      if (avatarFile) {
        formData.append('avatarFile', avatarFile);
      }
      if (clearAvatar) {
        formData.append('clearAvatar', 'true');
      }
      const updated = await api.updateProfile(formData);
      notify('个人资料已更新');
      onSaved(updated);
      if (updated.defaultLocale && updated.defaultLocale !== locale) {
        setLocale(updated.defaultLocale);
      }
      onClose();
    } catch (cause) {
      setError(cause.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title="个人资料设置"
      description="管理个人显示名称、头像及默认界面语言。"
      onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>取消</Button><Button variant="primary" icon={User} loading={busy} onClick={save}>保存资料</Button></>}
    >
      <div className="modal-form profile-modal-form">
        {error && <div className="modal-form-full"><Callout tone="danger" title="无法更新个人资料">{error}</Callout></div>}
        <div className="modal-form-full profile-avatar-section">
          <div className="profile-avatar-preview">
            <Avatar name={name || user?.name || ''} avatarUrl={avatarPreview} size="xl" />
          </div>
          <div className="profile-avatar-controls">
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={handleFileChange}
            />
            <div className="profile-avatar-btn-row">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={Upload}
                onClick={() => fileInputRef.current?.click()}
              >
                {avatarPreview ? '更换头像' : '上传头像'}
              </Button>
              {avatarPreview && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  icon={Trash2}
                  onClick={handleRemoveAvatar}
                >
                  清除头像
                </Button>
              )}
            </div>
            <small className="field-hint">支持 JPG、PNG、WEBP，最大 10MB</small>
          </div>
        </div>
        <label className="modal-form-full">
          <span>显示姓名 *</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="请输入姓名"
          />
        </label>
        <label className="modal-form-full">
          <span>界面默认语言</span>
          <select
            value={defaultLocale}
            onChange={(e) => setDefaultLocale(e.target.value)}
          >
            <option value="en">English (默认英文)</option>
            <option value="zh-CN">简体中文</option>
          </select>
        </label>
      </div>
    </Modal>
  );
}

export default function App() {
  const [route, setRoute] = useState(routeFromHash);
  const [state, setState] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [auth, setAuth] = useState({ loading: true, authenticated: false, setupRequired: false, user: null });
  const [loading, setLoading] = useState(true);
  const [taskOpen, setTaskOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [locale, setLocaleState] = useState(() => {
    const saved = window.localStorage.getItem('talent-locale');
    return SUPPORTED_LOCALES.includes(saved) ? saved : DEFAULT_LOCALE;
  });
  const lastLoadedLocale = useRef(locale);

  useUiLocalization(locale);

  const setLocale = useCallback((nextLocale) => {
    const normalized = SUPPORTED_LOCALES.includes(nextLocale) ? nextLocale : DEFAULT_LOCALE;
    window.localStorage.setItem('talent-locale', normalized);
    setLocaleState(normalized);
  }, []);

  const notify = useCallback((message, tone = 'success') => {
    setToast({ id: Date.now(), message, tone });
  }, []);

  const refresh = useCallback(async (jobId = selectedJobId, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const next = await api.state(jobId || undefined);
      setState(next);
      setSelectedJobId(next.selectedJobId || '');
      setAuth((current) => ({ ...current, loading: false, authenticated: true, setupRequired: false, user: next.currentUser }));
    } catch (error) {
      if (error.status === 401) {
        setState(null);
        setAuth((current) => ({ ...current, loading: false, authenticated: false, user: null }));
      } else notify(error.message, 'danger');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedJobId, notify]);

  useEffect(() => {
    let active = true;
    const boot = async () => {
      try {
        const status = await api.authStatus();
        if (!active) return;
        setAuth({ loading: false, ...status });
        if (status.authenticated) await refresh('', false);
        else setLoading(false);
      } catch (error) {
        if (active) { setAuth({ loading: false, authenticated: false, setupRequired: false, user: null }); setLoading(false); notify(error.message, 'danger'); }
      }
    };
    boot();
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const expired = () => { setState(null); setAuth((current) => ({ ...current, authenticated: false, user: null })); };
    window.addEventListener('talent-auth-required', expired);
    return () => window.removeEventListener('talent-auth-required', expired);
  }, []);
  useEffect(() => {
    const handler = () => setRoute(routeFromHash());
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
    setMobileOpen(false);
  }, [route.page, route.candidateId]);
  useEffect(() => {
    if (!state?.tasks?.some((task) => ['queued', 'processing'].includes(task.status))) return undefined;
    const timer = setInterval(() => refresh(selectedJobId, true), 1800);
    return () => clearInterval(timer);
  }, [state?.tasks, selectedJobId, refresh]);
  useEffect(() => {
    if (lastLoadedLocale.current === locale) return;
    lastLoadedLocale.current = locale;
    if (auth.authenticated && state) refresh(selectedJobId, true);
  }, [auth.authenticated, locale, refresh, selectedJobId, state]);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4200);
    return () => clearTimeout(timer);
  }, [toast]);

  const navigate = useCallback((page, id, returnJobId) => {
    window.location.hash = buildRouteHash(page, id, returnJobId);
  }, []);

  const onAuthenticated = async (result) => {
    setAuth({ loading: false, ...result });
    if (result.user?.defaultLocale) {
      setLocale(result.user.defaultLocale);
    }
    await refresh('', false);
    navigate('overview');
  };

  const logout = async () => {
    try { await api.logout(); } catch { /* Clear local state even if the session already expired. */ }
    setState(null);
    setSelectedJobId('');
    setAuth({ loading: false, authenticated: false, setupRequired: false, user: null });
  };

  const context = useMemo(() => ({
    state, selectedJobId, routeJobId: route.routeJobId, navigate, refresh: () => refresh('', false), notify, locale,
  }), [state, selectedJobId, route.routeJobId, navigate, refresh, notify, locale]);

  if (auth.loading) return <div className="app-loading"><div className="loading-mark">TI</div><Skeleton rows={5} /></div>;
  if (!auth.authenticated) return <AuthPage setupRequired={auth.setupRequired} onAuthenticated={onAuthenticated} />;
  if (auth.user?.mustChangePassword) return <AuthPage mustChange user={auth.user} onAuthenticated={onAuthenticated} />;

  let content;
  if (loading && !state) {
    content = <div className="app-loading"><div className="loading-mark">TI</div><Skeleton rows={5} /></div>;
  } else {
    const pages = {
      overview: <OverviewPage {...context} />,
      candidates: <CandidatesPage {...context} />,
      import: <ImportPage {...context} />,
      jobs: <JobsPage {...context} />,
      models: <ModelsPage {...context} />,
      calibration: <CalibrationPage {...context} />,
      'talent-library': <TalentLibraryPage {...context} />,
      audit: <AuditPage {...context} />,
      accounts: <AccountsPage {...context} />,
      departments: <DepartmentsPage {...context} />,
      candidate: <CandidateDetail {...context} candidateId={route.candidateId} returnJobId={route.returnJobId} />,
    };
    const role = state?.currentUser?.role;
    content = PAGE_ROLES[route.page]?.includes(role) ? pages[route.page] : pages.overview;
  }

  return (
    <>
      <AppShell
        state={state}
        page={route.page}
        routeJobId={route.routeJobId}
        navigate={navigate}
        onLogout={logout}
        onSecurity={() => setPasswordOpen(true)}
        onProfile={() => setProfileOpen(true)}
        taskOpen={taskOpen}
        setTaskOpen={setTaskOpen}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        locale={locale}
        setLocale={setLocale}
      >
        {content}
      </AppShell>
      <TaskDrawer
        open={taskOpen}
        tasks={state?.tasks || []}
        onClose={() => setTaskOpen(false)}
        onRetry={async (id) => {
          try { await api.retryTask(id); notify('任务已重新排队'); await refresh('', true); }
          catch (error) { notify(error.message, 'danger'); }
        }}
        onCancel={async (id) => {
          try { await api.cancelTask(id); notify('任务已取消'); await refresh('', true); }
          catch (error) { notify(error.message, 'danger'); }
        }}
      />
      <PasswordModal open={passwordOpen} onClose={() => setPasswordOpen(false)} notify={notify} onSaved={(user) => setAuth((current) => ({ ...current, user }))} />
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} user={state?.currentUser || auth.user} onSaved={(user) => { setAuth((current) => ({ ...current, user })); refresh('', true); }} notify={notify} locale={locale} setLocale={setLocale} />
      {toast && (
        <div className={`toast toast-${toast.tone}`}>
          {toast.tone === 'danger' ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
          <span>{toast.message}</span>
          <IconButton icon={X} label="关闭" onClick={() => setToast(null)} />
        </div>
      )}
    </>
  );
}
